import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../shared/hooks/useAuth';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  IconButton, 
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Button,
  TextField,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Dialog,
  DialogContent,
  Badge,
  Autocomplete
} from '@mui/material';
import { 
  FolderOpen,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  CloudUpload,
  Preview,
  Clear,
  Check,
  Close,
  Settings,
  Science,
  Article,
  Description,
  TextSnippet
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api, { getAuthHeaders } from '../../shared/services/api';
import { API_ENDPOINTS } from '../../shared/constants/apiEndpoints';
import { prepFileService } from './services/prepFileService';
import { useStructureSelection } from './hooks/useStructureSelection';
import { usePanelState } from './hooks/usePanelState';
import { usePreviewData } from './hooks/usePreviewData';
import { useUploadManagement } from './hooks/useUploadManagement';
import { useTreeData } from './hooks/useTreeData';
import { useColumnConfiguration } from './hooks/useColumnConfiguration';
import { useDataFiltering } from './hooks/useDataFiltering';
import { usePatternExtraction } from './hooks/usePatternExtraction';
import { usePanelRefs } from './hooks/usePanelRefs';
import { useNotification } from './hooks/useNotification';
import ReviewPanel from './panels/ReviewPanel';
import DraggablePaper from '../../shared/components/DraggablePaper';
import LeftSidebar from './containers/LeftSidebar';
import CenterContent from './containers/CenterContent';
import RightSidebar from './containers/RightSidebar';

export default function StructuresTab() {
  const theme = useTheme();
  const { user } = useAuth();
  const [prepFiles, setPrepFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use tree data hook (consolidates all tree-related state and logic)
  const {
    treeData,
    treeSelection,
    setTreeSelection,
    selectedNode,
    setSelectedNode,
    expandedItems,
    setExpandedItems,
    filteredData,
    setFilteredData,
    filterCriteria,
    setFilterCriteria,
    applyTreeFilter,
    expandToNode,
    handleTreeSelectionChange: handleTreeSelectionChangeBase,
    clearTreeFilter,
    restoreFilter
  } = useTreeData(prepFiles);
  
  // Use custom hook for selection management
  const {
    selectedPrepIds,
    setSelectedPrepIds,
    selectedFiles,
    selectedCount,
    applyRowSelection
  } = useStructureSelection(filteredData);
  
  // Use panel state hook
  const {
    activeSidebarView,
    setActiveSidebarView,
    isLeftPanelCollapsed,
    setIsLeftPanelCollapsed,
    isRightPanelCollapsed,
    setIsRightPanelCollapsed,
    bottomPanelOpen,
    setBottomPanelOpen,
    bottomPanelHeight,
    setBottomPanelHeight,
    applySidebarChange,
    openRightPanel
  } = usePanelState();
  
  // Use upload management hook
  const {
    uploadedFiles,
    setUploadedFiles,
    selectedUploadIds,
    setSelectedUploadIds,
    uploadProject,
    setUploadProject,
    uploadTarget,
    setUploadTarget,
    uploadBatchName,
    setUploadBatchName,
    isUploading,
    isDraggingOver,
    setIsDraggingOver,
    selectedUploadFileRows,
    transformedUploadFiles,
    handleFilesUploaded: handleFilesUploadedBase,
    handleSubmitUpload: handleSubmitUploadBase,
    handleClearUploadFiles,
    getUploadContext,
    updateUploadContext,
    generateUploadSuggestions
  } = useUploadManagement();
  
  // Use pattern extraction hook
  const {
    extractionPattern,
    setExtractionPattern,
    insertFieldPlaceholder,
    applyTemplate,
    getSampleFilenameForPattern,
    getPatternPreviewForExtraction
  } = usePatternExtraction();
  
  // Use panel refs hook
  const {
    patternPanelRef,
    metadataPanelRef,
    findReplacePanelRef,
    fmoPanelRef,
    resetPatternPanel,
    resetMetadataPanel,
    resetFindReplacePanel,
    resetPanels
  } = usePanelRefs();
  
  
  // Use data filtering hook
  const {
    quickFilterText,
    setQuickFilterText,
    displayData,
    searchSuggestions,
    clearSearch,
    isSearchActive,
    searchResultCount
  } = useDataFiltering(filteredData);
  
  // Use notification hook
  const {
    snackbar,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeNotification
  } = useNotification();
  
  // Help dialog state
  const [showBrowserHelp, setShowBrowserHelp] = useState(false);
  
  // Review panel states
  const [selectedPdbUrl, setSelectedPdbUrl] = useState(null);
  const [selectedFileForPreview, setSelectedFileForPreview] = useState(null);
  const [selectedStructureId, setSelectedStructureId] = useState(null);
  
  // Use preview data hook
  const {
    isPreviewMode,
    setIsPreviewMode,
    previewData,
    setPreviewData,
    patternExtractedData,
    setPatternExtractedData,
    metadataEditedData,
    setMetadataEditedData,
    currentPattern,
    setCurrentPattern,
    handlePreviewDataUpdate,
    cancelPreview
  } = usePreviewData();
  const fetchPrepFiles = useCallback(async () => {
    try {
      setLoading(true);
      const allPrepFiles = await prepFileService.fetchAll();
      
      setPrepFiles(allPrepFiles);
      setFilteredData(allPrepFiles);
      
      console.log('Fetched prep files:', allPrepFiles.length);
    } catch (error) {
      console.error('Failed to fetch prep files:', error);
      setPrepFiles([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrepFiles();
  }, [fetchPrepFiles]);

  // Use column configuration hook
  const {
    columns,
    columnVisibilityModel,
    setColumnVisibilityModel
  } = useColumnConfiguration({ isPreviewMode, previewData });
  const handleSidebarClick = (buttonId) => {
    // Clear ALL preview data when switching panels
    if ((activeSidebarView === 'pattern' || activeSidebarView === 'edit') && 
        buttonId !== activeSidebarView && isPreviewMode) {
      // Clear all preview data completely
      cancelPreview();
      
      // Reset the pattern panel if switching from it
      if (activeSidebarView === 'pattern' && patternPanelRef.current && patternPanelRef.current.reset) {
        patternPanelRef.current.reset();
      }
      
      // Reset the metadata panel if switching from it
      if (activeSidebarView === 'edit' && metadataPanelRef.current && metadataPanelRef.current.reset) {
        metadataPanelRef.current.reset();
      }

      // Reset the find/replace panel if switching from it
      if (activeSidebarView === 'findreplace' && findReplacePanelRef.current && findReplacePanelRef.current.reset) {
        findReplacePanelRef.current.reset();
      }
    }
    
    // Proceed with panel switch
    applySidebarChange(buttonId);
  };

  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    // Clear all preview data when changing selection
    if (isPreviewMode) {
      cancelPreview();
    }
    
    // Proceed with selection change
    updateSelectionAndPdbUrl(newRowSelectionModel);
  };
  
  const updateSelectionAndPdbUrl = (newRowSelectionModel) => {
    // Use the hook's selection handler
    const newSelection = applyRowSelection(newRowSelectionModel, displayData);
    
    if (!newSelection) return;
    
    // Update PDB URL for the last selected row (regardless of panel state)
    const selectedIdsArray = Array.from(newSelection);
    if (selectedIdsArray.length > 0) {
      const lastSelectedId = selectedIdsArray[selectedIdsArray.length - 1];
      const selectedRow = displayData.find(row => row.id === lastSelectedId);
      
      if (selectedRow?.id_) {
        // For existing prep files, use the API endpoint
        const pdbUrl = `${api.defaults.baseURL}${API_ENDPOINTS.PREPS.BY_ID(selectedRow.id_)}/pdb`;
        setSelectedPdbUrl(pdbUrl);
        setSelectedFileForPreview(null);
        setSelectedStructureId(selectedRow.id_);
      }
    } else {
      setSelectedPdbUrl(null);
      setSelectedStructureId(null);
    }
  };

  const handleFilesUploaded = useCallback((files) => {
    const count = handleFilesUploadedBase(files, {
      onSuccess: (message, severity = 'success') => {
        showNotification(message, severity);
      },
      onWarning: (message) => {
        showWarning(message);
      }
    });
    
    // Show the bottom panel when files are uploaded
    if (count > 0) {
      setBottomPanelOpen(true);
    }
  }, [handleFilesUploadedBase, showNotification, showWarning]);

  const handleSubmitUpload = useCallback(async () => {
    const response = await handleSubmitUploadBase({
      previewData,
      isPreviewMode,
      user,
      onProgress: (message) => {
        showInfo(message);
      },
      onSuccess: (message) => {
        // Clear preview mode if active
        if (isPreviewMode) {
          setIsPreviewMode(false);
          setPreviewData([]);
          setPatternExtractedData([]);
          setMetadataEditedData([]);
        }
        showSuccess(message);
      },
      onError: (message) => {
        showError(`Upload failed: ${message}`);
      },
      onComplete: async () => {
        // Refresh the main table to show newly uploaded files
        await fetchPrepFiles();
      }
    });
  }, [handleSubmitUploadBase, previewData, isPreviewMode, user, fetchPrepFiles, showInfo, showSuccess, showError]);

  // Handle canceling preview changes
  const handleCancelPreview = useCallback(() => {
    // Use hook's cancel function for core logic
    cancelPreview();
    
    // Reset pattern extraction panel completely (including pattern)
    if (patternPanelRef.current && patternPanelRef.current.reset) {
      patternPanelRef.current.reset();
    }
    
    // Discard changes in metadata editing panel (restore original values)
    if (metadataPanelRef.current && metadataPanelRef.current.discardChanges) {
      metadataPanelRef.current.discardChanges();
    }

    // Reset find/replace panel
    if (findReplacePanelRef.current && findReplacePanelRef.current.reset) {
      findReplacePanelRef.current.reset();
    }
    
    // The Browser filter and text filter will be preserved automatically
  }, [cancelPreview]);
  
  // Wrap clear upload files to add notification
  const handleClearUploadFilesWithNotification = useCallback(() => {
    handleClearUploadFiles();
    showInfo('Cleared all files');
  }, [handleClearUploadFiles, showInfo]);
  
  // Handle canceling upload (clears files and resets form)
  const handleCancelUpload = useCallback(() => {
    // Clear preview data
    setPreviewData([]);
    setCurrentPattern('');
    
    // Clear extracted data from both sources
    setPatternExtractedData([]);
    setMetadataEditedData([]);
    
    // Reset panels using the hook methods
    resetPanels(['pattern', 'metadata', 'findreplace']);

    setUploadedFiles([]);
    setSelectedUploadIds(new Set());
    updateUploadContext({ project: '', target: '', batch: '' });
    setBottomPanelOpen(false);
  }, [resetPanels, setPreviewData, setCurrentPattern, setPatternExtractedData, setMetadataEditedData, setUploadedFiles, setSelectedUploadIds, updateUploadContext, setBottomPanelOpen]);

  // Handle saving preview changes to backend
  const handleSavePreviewChanges = useCallback(async () => {
    if (!isPreviewMode || previewData.length === 0) {
      return;
    }

    try {
      // Prepare update requests using the service
      const updateRequests = prepFileService.prepareUpdateRequests(previewData, prepFiles);
      
      if (updateRequests.length === 0) {
        console.error('No valid update requests could be created');
        return;
      }

      // Call API using the service
      const response = await prepFileService.updateBulk(updateRequests);

      // Handle both 200 (OK) and 204 (No Content) as success
      if (response.status === 200 || response.status === 204) {
        const successMessage = `Successfully updated ${updateRequests.length} prep file${updateRequests.length !== 1 ? 's' : ''}`;
        
        // Turn off preview mode
        setIsPreviewMode(false);
        setPreviewData([]);
        
        // Clear extracted data from both sources
        setPatternExtractedData([]);
        setMetadataEditedData([]);
        
        // If filter criteria exists and any of the filtered fields were modified,
        // update the filter criteria with the new values
        if (filterCriteria && updateRequests.length > 0) {
          const updatedCriteria = { ...filterCriteria };
          let criteriaChanged = false;
          
          // Since all selected items are from the same filter, if the filtered field
          // was updated, they should all have the same new value
          // Just check the first update request
          const firstUpdate = updateRequests[0];
          
          if (filterCriteria.project && firstUpdate.project && firstUpdate.project !== filterCriteria.project) {
            updatedCriteria.project = firstUpdate.project;
            criteriaChanged = true;
          }
          if (filterCriteria.target && firstUpdate.target && firstUpdate.target !== filterCriteria.target) {
            updatedCriteria.target = firstUpdate.target;
            criteriaChanged = true;
          }
          if (filterCriteria.batch && firstUpdate.batch && firstUpdate.batch !== filterCriteria.batch) {
            updatedCriteria.batch = firstUpdate.batch;
            criteriaChanged = true;
          }
          
          if (criteriaChanged) {
            setFilterCriteria(updatedCriteria);
          }
        }
        
        // Refresh data to show updated values
        await fetchPrepFiles();
        
        // Show success message using notification hook
        showSuccess(successMessage);
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      // Show error message using notification hook
      showError(`Failed to save changes: ${error.response?.data?.detail || error.message}`);
    }
  }, [isPreviewMode, previewData, fetchPrepFiles, filterCriteria]);



  // Effect to reapply filter when prepFiles updates
  useEffect(() => {
    if (filterCriteria) {
      // Apply filter using stored criteria values
      let filtered = prepFiles;
      
      if (filterCriteria.project) {
        filtered = filtered.filter(prep => prep.project === filterCriteria.project);
      }
      if (filterCriteria.target) {
        filtered = filtered.filter(prep => prep.target === filterCriteria.target);
      }
      if (filterCriteria.batch) {
        filtered = filtered.filter(prep => prep.batch === filterCriteria.batch);
      }
      
      setFilteredData(filtered);
      
      // Try to reconstruct the tree selection if possible
      if (filterCriteria.batches) {
        // Multiple batches stored
        const nodeIds = filterCriteria.batches.map(batch =>
          `batch::${batch.project}::${batch.target}::${batch.batch}`
        );
        setTreeSelection(nodeIds);
      } else if (filterCriteria.batch) {
        setTreeSelection([`batch::${filterCriteria.project}::${filterCriteria.target}::${filterCriteria.batch}`]);
      } else if (filterCriteria.target) {
        setTreeSelection([`target::${filterCriteria.project}::${filterCriteria.target}`]);
      } else if (filterCriteria.project) {
        setTreeSelection([`project::${filterCriteria.project}`]);
      }
    } else if (treeSelection && treeSelection.length > 0) {
      // Fall back to tree selection if no criteria stored
      applyTreeFilter(treeSelection);
    } else {
      setFilteredData(prepFiles);
    }
  }, [prepFiles]);

  // Wrap tree selection change to handle upload context and selection
  const handleTreeSelectionChange = (event, nodeIds) => {
    const result = handleTreeSelectionChangeBase(event, nodeIds);

    if (!nodeIds || nodeIds.length === 0) {
      setSelectedPrepIds(new Set());
      setSelectedPdbUrl(null);
      setSelectedFileForPreview(null);
      setSelectedStructureId(null);
      return;
    }

    // Update upload context based on selection (use first batch for upload context)
    if (result.parsedNodes && result.parsedNodes.length > 0) {
      const parsed = result.parsedNodes[0];
      if (parsed.type === 'project') {
        updateUploadContext({
          project: parsed.project,
          target: '',
          batch: ''
        });
      } else if (parsed.type === 'target') {
        updateUploadContext({
          project: parsed.project,
          target: parsed.target,
          batch: ''
        });
      } else if (parsed.type === 'batch') {
        updateUploadContext({
          project: parsed.project,
          target: parsed.target,
          batch: parsed.batch
        });
      }
    } else if (result.parsedNode) {
      // Backward compatibility for single selection
      const parsed = result.parsedNode;
      if (parsed.type === 'project') {
        updateUploadContext({
          project: parsed.project,
          target: '',
          batch: ''
        });
      } else if (parsed.type === 'target') {
        updateUploadContext({
          project: parsed.project,
          target: parsed.target,
          batch: ''
        });
      } else if (parsed.type === 'batch') {
        updateUploadContext({
          project: parsed.project,
          target: parsed.target,
          batch: parsed.batch
        });
      }
    }
    
    // Auto-select all items if result indicates it
    if (result.shouldAutoSelect && result.filtered && result.filtered.length > 0) {
      const selectedIds = new Set(result.filtered.map(prep => prep.id));
      setSelectedPrepIds(selectedIds);
      
      // Set the review panel to show the first filtered structure
      const firstFilteredItem = result.filtered[0];
      if (firstFilteredItem?.id_) {
        const pdbUrl = `${api.defaults.baseURL}${API_ENDPOINTS.PREPS.BY_ID(firstFilteredItem.id_)}/pdb`;
        setSelectedPdbUrl(pdbUrl);
        setSelectedFileForPreview(null);
        setSelectedStructureId(firstFilteredItem.id_);
      }
    } else {
      setSelectedPdbUrl(null);
      setSelectedFileForPreview(null);
      setSelectedStructureId(null);
    }
  };

  // Generate upload suggestions using the hook
  const uploadSuggestions = useMemo(() => 
    generateUploadSuggestions(prepFiles),
    [prepFiles, generateUploadSuggestions]
  );
  
  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100%', 
      overflow: 'hidden'
    }}>
      {/* Left Section with Sidebar */}
      <LeftSidebar
        // Panel state
        activeSidebarView={activeSidebarView}
        isLeftPanelCollapsed={isLeftPanelCollapsed}
        setIsLeftPanelCollapsed={setIsLeftPanelCollapsed}
        onSidebarClick={handleSidebarClick}
        
        // Browser panel props
        treeData={treeData}
        treeSelection={treeSelection}
        onTreeSelectionChange={handleTreeSelectionChange}
        expandedItems={expandedItems}
        onExpandedItemsChange={setExpandedItems}
        loading={loading}
        filteredCount={filteredData.length}
        selectedCount={selectedPrepIds.size}
        totalCount={prepFiles.length}
        onClearFilter={() => {
          clearTreeFilter();
          setSelectedPrepIds(new Set());
          // Clear upload fields
          updateUploadContext({ project: '', target: '', batch: '' });
        }}
        showBrowserHelp={showBrowserHelp}
        onBrowserHelpToggle={setShowBrowserHelp}
        
        // Pattern panel props
        patternPanelRef={patternPanelRef}
        selectedRows={selectedFiles}
        uploadFiles={uploadedFiles}
        selectedUploadRows={selectedUploadFileRows}
        onPatternApply={fetchPrepFiles}
        onPreviewUpdate={handlePreviewDataUpdate}
        previewMode={isPreviewMode}
        
        // Metadata panel props
        metadataPanelRef={metadataPanelRef}
        allPrepFiles={prepFiles}

        // Find Replace panel props
        findReplacePanelRef={findReplacePanelRef}

        // FMO panel props
        fmoPanelRef={fmoPanelRef}
        preps={filteredData}
        onFragmentationStart={(entries) => {
          console.log('FMO entries created:', entries);
          showSuccess(`Successfully created ${entries.length} FMO input set${entries.length !== 1 ? 's' : ''}`);
        }}
      />

      {/* Center Content Area with Dynamic Bottom Panel */}
      <CenterContent
        // Grid props
        displayData={displayData}
        columns={columns}
        loading={loading}
        selectedIds={selectedPrepIds}
        onSelectionChange={handleRowSelectionModelChange}
        onRowClick={(params) => {
          // Set PDB URL when row is clicked (regardless of panel state)
          if (params.row?.id_) {
            const pdbUrl = `${api.defaults.baseURL}${API_ENDPOINTS.PREPS.BY_ID(params.row.id_)}/pdb`;
            setSelectedPdbUrl(pdbUrl);
            setSelectedFileForPreview(null);
            setSelectedStructureId(params.row.id_);
          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityChange={setColumnVisibilityModel}
        isPreviewMode={isPreviewMode}
        previewData={previewData}
        onCancelPreview={handleCancelPreview}
        onSaveChanges={handleSavePreviewChanges}
        searchTerm={quickFilterText}
        onSearchChange={setQuickFilterText}
        searchOptions={searchSuggestions}
        
        // Upload panel props
        bottomPanelOpen={bottomPanelOpen}
        setBottomPanelOpen={setBottomPanelOpen}
        bottomPanelHeight={bottomPanelHeight}
        setBottomPanelHeight={setBottomPanelHeight}
        uploadedFiles={transformedUploadFiles}
        onFilesUploaded={handleFilesUploaded}
        onClearFiles={handleClearUploadFilesWithNotification}
        selectedUploadIds={selectedUploadIds}
        onUploadSelectionChange={(newRowSelectionModel) => {
          // DataGrid Pro uses object format: { ids: Set, type: 'include' | 'exclude' }
          let newSelection;
          
          if (newRowSelectionModel && typeof newRowSelectionModel === 'object' && newRowSelectionModel.ids instanceof Set) {
            if (newRowSelectionModel.type === 'exclude') {
              // 'exclude' with empty set means select all
              // 'exclude' with some IDs means select all except those IDs
              if (newRowSelectionModel.ids.size === 0) {
                // Select all rows
                newSelection = new Set(uploadedFiles.map((_, index) => index));
              } else {
                // Select all except the excluded IDs
                const excludedIds = newRowSelectionModel.ids;
                newSelection = new Set(uploadedFiles.map((_, index) => index).filter(id => !excludedIds.has(id)));
              }
            } else {
              // 'include' means only these IDs are selected (normal case)
              newSelection = newRowSelectionModel.ids;
            }
          } else {
            console.warn('Unexpected upload selection model format:', newRowSelectionModel);
            return;
          }
          
          setSelectedUploadIds(newSelection);
        }}
        uploadProject={uploadProject}
        onProjectChange={setUploadProject}
        uploadTarget={uploadTarget}
        onTargetChange={setUploadTarget}
        uploadBatch={uploadBatchName}
        onBatchChange={setUploadBatchName}
        uploadSuggestions={uploadSuggestions}
        onSubmitUpload={handleSubmitUpload}
        onCancelUpload={handleCancelUpload}
        isUploading={isUploading}
        onFileClick={(params) => {
          // Preview the file when clicked
          if (params.row._file) {
            setSelectedFileForPreview(params.row._file);
            setSelectedPdbUrl(null);
            setSelectedStructureId(null);
          }
        }}
      />

      {/* Right Panel - Review */}
      <ReviewPanel
        isCollapsed={isRightPanelCollapsed}
        onCollapse={() => setIsRightPanelCollapsed(true)}
        selectedPdbUrl={selectedPdbUrl}
        selectedFile={selectedFileForPreview}
        selectedStructureId={selectedStructureId}
      />

      {/* Right Sidebar */}
      <RightSidebar
        isRightPanelCollapsed={isRightPanelCollapsed}
        onTogglePanel={setIsRightPanelCollapsed}
        onPanelOpen={() => {
          // If opening the panel and there are selected rows, set the PDB URL
          if (selectedPrepIds.size > 0) {
            const selectedIdsArray = Array.from(selectedPrepIds);
            const lastSelectedId = selectedIdsArray[selectedIdsArray.length - 1];
            const selectedRow = displayData.find(row => row.id === lastSelectedId);
            
            if (selectedRow?.id_) {
              const pdbUrl = `${api.defaults.baseURL}${API_ENDPOINTS.PREPS.BY_ID(selectedRow.id_)}/pdb`;
              setSelectedPdbUrl(pdbUrl);
              setSelectedFileForPreview(null);
              setSelectedStructureId(selectedRow.id_);
            }
          }
        }}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeNotification}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
